import type { DependencyGraph, ForceGraphData } from '../types/graph';

interface StatsPanelProps {
  rawData: DependencyGraph | null;
  filteredData: ForceGraphData;
}

export function StatsPanel({ rawData, filteredData }: StatsPanelProps) {
  if (!rawData) return null;

  const stats = rawData.statistics;
  const isFiltered = filteredData.nodes.length !== stats.totalNodes;

  return (
    <div className="hud-panel" style={{ padding: 'var(--space-xl)' }}>
      <h3 style={{ margin: '0 0 var(--space-lg) 0', fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>
        NILAssist Architecture
      </h3>

      {isFiltered && (
        <div style={{
          background: 'var(--color-accent)',
          color: 'var(--color-text-primary)',
          padding: 'var(--space-sm) var(--space-md)',
          borderRadius: '0.25rem',
          marginBottom: 'var(--space-lg)',
          fontSize: 'var(--font-size-sm)'
        }}>
          Filtered: {filteredData.nodes.length} / {stats.totalNodes} nodes
        </div>
      )}

      <Section title="Overview">
        <Stat label="Total Files" value={stats.totalNodes} />
        <Stat label="Total Dependencies" value={stats.totalEdges} />
        <Stat label="Cross-Service Calls" value={stats.crossServiceEdges} highlight />
        <Stat label="Circular Dependencies" value={rawData.cycles.length} highlight={rawData.cycles.length > 0} />
      </Section>

      <Section title="By Service">
        {Object.entries(stats.nodesByService).map(([service, count]) => (
          <Stat key={service} label={service} value={count} />
        ))}
      </Section>

      <Section title="By Language">
        {Object.entries(stats.nodesByLanguage).map(([lang, count]) => (
          <Stat key={lang} label={lang} value={count} />
        ))}
      </Section>

      <Section title="Edge Types">
        {Object.entries(stats.edgesByType).map(([type, count]) => (
          <Stat key={type} label={type} value={count} />
        ))}
      </Section>

      {rawData.cycles.length > 0 && (
        <Section title="Circular Dependencies">
          {rawData.cycles.map((cycle, idx) => (
            <div key={idx} style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-danger)',
              padding: 'var(--space-xs) 0',
              borderBottom: idx < rawData.cycles.length - 1 ? '1px solid #222' : 'none'
            }}>
              {cycle.path.slice(0, 2).map(p => p.split('/').pop()).join(' \u2194 ')}
            </div>
          ))}
        </Section>
      )}

      <div style={{
        marginTop: 'var(--space-xl)',
        paddingTop: 'var(--space-lg)',
        borderTop: '1px solid var(--color-border)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-muted)'
      }}>
        Generated: {new Date(rawData.metadata.generatedAt).toLocaleDateString()}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 'var(--space-xl)' }}>
      <h4 style={{
        margin: '0 0 var(--space-sm) 0',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 600,
        color: 'var(--color-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.03125rem'
      }}>
        {title}
      </h4>
      {children}
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: 'var(--space-xs) 0',
      borderBottom: '1px solid #222'
    }}>
      <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      <span style={{
        fontWeight: 600,
        color: highlight ? 'var(--color-danger)' : 'var(--color-text-primary)'
      }}>
        {value.toLocaleString()}
      </span>
    </div>
  );
}
